import { useState } from "react";
import { investigatorNews, type NewsItem } from "@/features/news/newsItems";
import { getViewedNewsIds, markNewsAsViewed } from "@/shared/utils/newsStorage";

interface Presentation {
  userId: string;
  items: readonly NewsItem[];
  phase: "open" | "exiting";
}

export function useNewsModal(userId: string | undefined, enabled = true) {
  const [dismissedByUser, setDismissedByUser] = useState<
    Record<string, string[]>
  >({});
  const [replay, setReplay] = useState({
    userId,
    enabled,
    requested: false,
  });
  const [presentation, setPresentation] = useState<Presentation | null>(null);
  const sameScope = replay.userId === userId && replay.enabled === enabled;
  // Reset manual replay before children render under a new user or flag state.
  if (!sameScope) setReplay({ userId, enabled, requested: false });

  const dismissedIds = new Set(
    userId
      ? [...getViewedNewsIds(userId), ...(dismissedByUser[userId] ?? [])]
      : []
  );
  const canOpen = enabled && !!userId && investigatorNews.length > 0;
  const activeItems = canOpen
    ? investigatorNews.filter(
        (item) =>
          (sameScope && replay.requested) || !dismissedIds.has(item.newsId)
      )
    : [];
  const open = activeItems.length > 0;

  // Retain the current presentation through Fade's exit. Compare the catalog's
  // stable item references, not the newly filtered array, to avoid render loops.
  if (
    open &&
    userId &&
    (presentation?.userId !== userId ||
      presentation.phase !== "open" ||
      presentation.items.length !== activeItems.length ||
      presentation.items.some((item, index) => item !== activeItems[index]))
  ) {
    setPresentation({ userId, items: activeItems, phase: "open" });
  } else if (!open && presentation?.phase === "open") {
    setPresentation({ ...presentation, phase: "exiting" });
  }

  const handleOpen = () => {
    if (canOpen) setReplay({ userId, enabled, requested: true });
  };

  const handleClose = () => {
    // A fading snapshot can belong to the previous account after a user switch.
    if (!open || !presentation || presentation.userId !== userId) return;
    const owner = presentation.userId;
    const ids = presentation.items.map((item) => item.newsId);
    markNewsAsViewed(owner, ids);
    setDismissedByUser((previous) => ({
      ...previous,
      [owner]: [...new Set([...(previous[owner] ?? []), ...ids])],
    }));
    setReplay({ userId, enabled, requested: false });
  };

  const handleExited = () => {
    // An interrupted exit must not clear a presentation that has reopened.
    setPresentation((current) =>
      current === presentation && current?.phase === "exiting" ? null : current
    );
  };

  return {
    newsItems: presentation?.items ?? [],
    open,
    present: presentation !== null,
    canOpen,
    handleOpen,
    handleClose,
    handleExited,
  };
}
